/**
 * Reusable date and time formatting utilities
 */

/**
 * Format a date in a readable format
 * @param {string|Date} date - The date to format
 * @param {string} format - Format type: 'full', 'short', 'medium', 'long', 'date-only'
 * @returns {string} Formatted date string
 */
export const formatDate = (date, format = 'medium') => {
  if (!date) return 'N/A';

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  switch (format) {
    case 'full':
      // Example: "Monday, December 24, 2025"
      return dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

    case 'long':
      // Example: "December 24, 2025"
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

    case 'medium':
      // Example: "Dec 24, 2025"
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

    case 'short':
      // Example: "12/24/2025"
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric'
      });

    case 'date-only':
      // Example: "Dec 24"
      return dateObj.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });

    default:
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
  }
};

/**
 * Format a time in a readable format
 * @param {string|Date} time - The time to format (can be time string "07:00:00", datetime string, or Date object)
 * @param {object} options - Formatting options
 * @returns {string} Formatted time string
 */
export const formatTime = (time, options = {}) => {
  if (!time) return 'N/A';

  let dateObj;
  if (time instanceof Date) {
    dateObj = time;
  } else if (typeof time === 'string') {
    // Handle ISO datetime strings (e.g., "2025-12-24T07:00:00.000000Z")
    if (time.includes('T') || time.includes(' ')) {
      dateObj = new Date(time);
    } else if (time.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
      // Handle time-only strings like "07:00:00" or "07:00" or "7:00"
      const parts = time.split(':');
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      
      // Create a date object with today's date and the specified time
      dateObj = new Date();
      dateObj.setHours(hours, minutes, 0, 0);
    } else {
      // Try to parse as a date
      dateObj = new Date(time);
    }
  } else {
    dateObj = new Date(time);
  }

  // Check if date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Invalid Time';
  }

  const defaultOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };

  return dateObj.toLocaleTimeString('en-US', defaultOptions);
};

/**
 * Format a date and time together
 * @param {string|Date} dateTime - The date and time to format
 * @param {string} dateFormat - Format for the date part
 * @param {object} timeOptions - Options for the time part
 * @returns {string} Formatted date and time string
 */
export const formatDateTime = (dateTime, dateFormat = 'medium', timeOptions = {}) => {
  if (!dateTime) return 'N/A';

  const dateStr = formatDate(dateTime, dateFormat);
  const timeStr = formatTime(dateTime, timeOptions);

  return `${dateStr} at ${timeStr}`;
};

/**
 * Format a date with time in a compact format
 * @param {string|Date} dateTime - The date and time to format
 * @returns {object} Object with formatted date and time separately
 */
export const formatDateWithTime = (dateTime) => {
  if (!dateTime) {
    return {
      date: 'N/A',
      time: 'N/A'
    };
  }

  return {
    date: formatDate(dateTime, 'medium'),
    time: formatTime(dateTime)
  };
};

/**
 * Get relative time (e.g., "2 days ago", "in 3 weeks")
 * @param {string|Date} date - The date to format
 * @returns {string} Relative time string
 */
export const getRelativeTime = (date) => {
  if (!date) return 'N/A';

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const now = new Date();
  const diffMs = dateObj - now;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  if (Math.abs(diffSecs) < 60) {
    return diffSecs < 0 ? 'just now' : 'in a moment';
  } else if (Math.abs(diffMins) < 60) {
    return diffMins < 0 ? `${Math.abs(diffMins)} minutes ago` : `in ${diffMins} minutes`;
  } else if (Math.abs(diffHours) < 24) {
    return diffHours < 0 ? `${Math.abs(diffHours)} hours ago` : `in ${diffHours} hours`;
  } else if (Math.abs(diffDays) < 7) {
    return diffDays < 0 ? `${Math.abs(diffDays)} days ago` : `in ${diffDays} days`;
  } else if (Math.abs(diffWeeks) < 4) {
    return diffWeeks < 0 ? `${Math.abs(diffWeeks)} weeks ago` : `in ${diffWeeks} weeks`;
  } else if (Math.abs(diffMonths) < 12) {
    return diffMonths < 0 ? `${Math.abs(diffMonths)} months ago` : `in ${diffMonths} months`;
  } else {
    return diffYears < 0 ? `${Math.abs(diffYears)} years ago` : `in ${diffYears} years`;
  }
};

/**
 * Check if a date is today
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is today
 */
export const isToday = (date) => {
  if (!date) return false;

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) {
    return false;
  }

  const today = new Date();
  return (
    dateObj.getDate() === today.getDate() &&
    dateObj.getMonth() === today.getMonth() &&
    dateObj.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is in the past
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the past
 */
export const isPast = (date) => {
  if (!date) return false;

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) {
    return false;
  }

  return dateObj < new Date();
};

/**
 * Check if a date is in the future
 * @param {string|Date} date - The date to check
 * @returns {boolean} True if the date is in the future
 */
export const isFuture = (date) => {
  if (!date) return false;

  let dateObj;
  if (date instanceof Date) {
    dateObj = date;
  } else {
    dateObj = new Date(date);
  }

  if (isNaN(dateObj.getTime())) {
    return false;
  }

  return dateObj > new Date();
};

